import { Component, Output, Input, EventEmitter, OnDestroy } from '@angular/core';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, take, takeWhile } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';

import { MonitorsService } from '../../core/services/http-services/monitors.service';
import { MachinesService } from '../../core/services/http-services/machines.service';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { AuthService } from '../../core/services/auth/auth.service';
import { UIService } from '../../core/services/utils/ui.service';
import { MonitorChangeViewService } from '../../core/services/http-services/monitor-change-view.service';
import { PlaylistItemsService } from '../../core/services/http-services/playlistItems.service';
import { MonitorHub } from '../../shared-module/hubs/monitor.hub';
import { AlertService } from '../../wall-module/shared/services/alert.service';
import { RemoteControlService } from '../../core/services/http-services/remote-control.service';
import { MachineCategoriesService } from '../../core/services/http-services/machine-categories.service';

import { AppConstants } from '../../app.constants';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { ViewMode } from '../../core/enums/view-mode.enum';
import { Machine } from '../../core/models/entities/machine.type';
import { MonitorFilterDialogMessagesEnum } from '../../core/enums/toastr-messages/monitor-filter-dialog-messages.enum';
import { MonitorFilterPipe } from '../../core/pipes/monitor-filter.pipe';
import { MonitorTagFilterPipe } from '../../core/pipes/monitor-tag-filter.pipe';
import { ConfirmationConfig } from '../../wall-module/shared/models/confirmation-config.model';
import { DialogMessagesEnum } from '../../wall-module/shared/enums/dialog-messages.enum';
import { MachineCategory } from '../../core/models/entities/machine-category.type';

@Component({
  selector: 'monitors-filter',
  templateUrl: './monitors-filter.component.html',
  styleUrls: ['./monitors-filter.component.scss'],
})
export class MonitorsFilterComponent implements OnDestroy {
  private _viewMode: ViewMode;
  private filterExpression: string;
  private screens: BehaviorScreen[] = [];
  private selectedScreens: BehaviorScreen[] = [];
  private subscriptions: Subscription = new Subscription();
  private identifySubscriptions: Subscription = new Subscription();
  private textFilterChanged: Subject<string> = new Subject();

  public viewModes = ViewMode;
  public locations: Machine[] = [];
  public location: Machine = new Machine({ Title: 'All locations' });
  public allLocations: Machine = new Machine({ Title: 'All locations' });
  public categories$: Observable<MachineCategory[]>;
  public selectedCategories: MachineCategory[];
  public isIdentifyEnabled: boolean;
  public isSimplifiedIdentifyEnabled: boolean;
  public isRelaunching: boolean;
  public remoteControlMonitorKey$: BehaviorSubject<MonitorKey>;

  @Input()
  get viewMode() {
    return this._viewMode;
  }

  @Output() viewModeChange = new EventEmitter();
  set viewMode(value) {
    this._viewMode = value;
    this.viewModeChange.emit(this._viewMode);
  }

  @Input()
  get locationFilter() {
    return this.location;
  }

  set locationFilter(value) {
    this.setUserLocation(null, value);
    this.updateIdentifyState();
  }

  @Input()
  get nameFilter() {
    return this.filterExpression;
  }

  set nameFilter(value) {
    this.filterExpression = value;
    this.screenStateService.updateMonitorVisibility(
      this.locationFilter,
      this.nameFilter
    );
    this.textFilterChanged.next(value);
  }

  get categoryFilter(): MachineCategory[] {
    return this.selectedCategories;
  }

  set categoryFilter(value) {
    this.selectedCategories = value.slice(-1);
    this.updateIdentifyState();
  }

  constructor(
    private readonly screenStateService: ScreensStateService,
    private readonly monDataService: MonitorsService,
    private readonly machineService: MachinesService,
    private readonly authService: AuthService,
    public uiService: UIService,
    private readonly toastrService: ToastrService,
    private readonly monitorChangeViewService: MonitorChangeViewService,
    private readonly monitorFilterPipe: MonitorFilterPipe,
    private readonly remoteControlService: RemoteControlService,
    private readonly monitorTagFilterPipe: MonitorTagFilterPipe,
    private readonly playlistItemsService: PlaylistItemsService,
    private readonly monitorHub: MonitorHub,
    private readonly alertService: AlertService,
    private readonly categoryService: MachineCategoriesService
  ) {
    const userId = JSON.parse(localStorage.getItem('authData'))['userId'];
    this.subscriptions.add(
      this.screenStateService.locationsChanged.subscribe((data) => {
        this.locations = [...(data ? data : [])];
        this.allLocations.ShowPrimaryScreen = _.every(
          data,
          (x) => x.ShowPrimaryScreen == true
        );
        this.locations.unshift(this.allLocations);
        this.checkIfSelectedLocationExists(data);
      })
    );
    this.subscriptions.add(
      this.machineService.getUserLocation(userId).subscribe((locations) => {
        this.setUserLocation(locations, this.locationFilter);
      })
    );
    this.subscriptions.add(
      this.screenStateService.selectedScreensChanged.subscribe(
        (data) => {
          this.selectedScreens = data;
          this.updateIdentification(this.selectedScreens?.length ? this.selectedScreens : this.screens);
        }
      )
    );
    this.subscriptions.add(
      this.screenStateService.screensCollectionChanged.subscribe(
        (data: BehaviorScreen[]) => {
          this.screens = data;
        }
      )
    );

    this.identifySubscriptions.add(
      this.screenStateService.isSplashEnabled
        .pipe(
          filter(isEnabled => !!isEnabled),
          takeWhile(() => !!!this.selectedScreens.length)
        ).subscribe(isEnabled =>
          this.isIdentifyEnabled = isEnabled
        )
    );

    this.identifySubscriptions.add(
      this.screenStateService.isSimplifiedSplashEnabled
        .pipe(
          filter(isEnabled => !!isEnabled),
          takeWhile(() => !!!this.selectedScreens.length)
        )
        .subscribe(isEnabled =>
          this.isSimplifiedIdentifyEnabled = isEnabled
        )
    );

    this.subscriptions.add(
      this.textFilterChanged.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe(() => this.updateIdentifyState())
    );

    this.subscriptions.add(
      this.monitorChangeViewService.monitorsViewChange.subscribe((viewMode: ViewMode) => this.viewMode = viewMode)
    );

    this.remoteControlMonitorKey$ = this.remoteControlService.remoteControlMonitorKey;

    this.categories$ = this.categoryService.getAllCategories()
      .pipe(
        takeUntilDestroyed(),
        map(categories => {
          const allCategoriesOption = new MachineCategory(AppConstants.MACHINE_ALL_CATEGORIES_NAME, null);

          this.selectedCategories = [allCategoriesOption];
          categories.unshift(allCategoriesOption);
          return categories;
        })
      )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.identifySubscriptions.unsubscribe();
  }

  onShowPrimaryChanged(item: Machine): void {
    let physicalAddresses: string[];

    if (!item.PhysicalAddress) {
      this.screenStateService.setShowPrimaryAll(!item.ShowPrimaryScreen);
      physicalAddresses = this.locations
        .filter((x) => x.PhysicalAddress)
        .map((x) => x.PhysicalAddress);
    } else {
      physicalAddresses = [item.PhysicalAddress];
    }

    if (physicalAddresses && physicalAddresses.length) {
      this.subscriptions.add(
        this.machineService
          .updateShowPrimaryFlag(physicalAddresses, !item.ShowPrimaryScreen)
          .subscribe()
      );
    }

    this.updateLocationsLink();
  }

  onChangeCheck(): void {
    this.allLocations.ShowPrimaryScreen = _.every(
      this.locations.filter((x) => x.PhysicalAddress),
      (x) => x.ShowPrimaryScreen == true
    );

    this.locationFilter = _.cloneDeep(this.locationFilter);
    this.updateLocationsLink();
  }

  onChangeMultiple(checked: boolean, selectedCategory: MachineCategory): void {
    const categoriesWithIds = this.selectedCategories.filter(category => category.Id);

    this.selectedCategories = checked ?
      [...categoriesWithIds, selectedCategory] :
        categoriesWithIds.filter(category => category.Id !== selectedCategory.Id);
  }

  onIdentifyChange(toggleChange: MatSlideToggleChange): void {
    if (toggleChange && toggleChange.checked) {
      this.enableIdentify();
    } else {
      this.disableIdentify();
    }

    this.identifySubscriptions.unsubscribe();
  }

  enableIdentify(screenNameOnly: boolean = false, screenNameToggleSwitched: boolean = false): void {
    if (this.selectedScreens && this.selectedScreens.length) {
      this.prepareMonitorsToIdentify(this.selectedScreens, screenNameOnly);
    } else {
      const filteredMonitors = this.monitorFilterPipe.transform(this.screens, this.location, this.nameFilter, this.selectedCategories) as BehaviorScreen[];
      const monitorsToIdentify = this.monitorTagFilterPipe.transform(filteredMonitors);

      monitorsToIdentify.pipe(
        map(monitors => screenNameToggleSwitched
          ? monitors.filter(mon => mon.ScreenState?.IsSplashEnabled)
          : monitors),
        take(1)
      ).subscribe((monitors: BehaviorScreen[]) => {
        this.prepareMonitorsToIdentify(monitors, screenNameOnly);
      });
    }
  }

  private prepareMonitorsToIdentify(monitors: BehaviorScreen[], screenNameOnly: boolean): void {
    if (monitors.length) {
      const keys: MonitorKey[] = _.map(
        monitors, (x: BehaviorScreen) => new MonitorKey(x.MonitorHandler, x.MachinePhysicalAddress)
      );
      this.subscriptions.add(
        this.monDataService.enableIdentifyScreens(keys, screenNameOnly).subscribe(() => {
          this.isSimplifiedIdentifyEnabled = screenNameOnly;

          monitors.forEach(screen => {
            if (screen.ScreenState) {
              screen.ScreenState.IsSplashEnabled = true;
            }
          });

          this.isIdentifyEnabled = true;
        })
      );
    } else {
      this.toastrService.warning(MonitorFilterDialogMessagesEnum.NoMonitorsSelectedDescription,
        MonitorFilterDialogMessagesEnum.NoMonitorsSelectedTitle);
    }
  }

  private updateIdentifyState(): void {
    if (this.isIdentifyEnabled) {
      this.disableIdentify();
      this.enableIdentify(this.isSimplifiedIdentifyEnabled);
    }
  }

  private disableIdentify(): void {
    this.subscriptions.add(
      this.monDataService.disableIdentifyScreens().subscribe(
        () => {
          this.screens.forEach(screen => {
            screen.ScreenState.IsSplashEnabled = false;
            screen.ScreenState.IsSimplifiedSplashEnabled = false;
          });
          this.isSimplifiedIdentifyEnabled = false;
          this.isIdentifyEnabled = false;
        }
      )
    );
  }

  setCurrentLocation(location: Machine): void {
    const UserId = this.authService.userId;

    this.subscriptions.add(
      this.machineService
        .setUserLocation({ UserId, PhysicalAddress: location.PhysicalAddress })
        .subscribe(() => (this.location = location))
    );
    if (location.PhysicalAddress) {
      this.screenStateService.selectedScreensChanged.next(
        this.selectedScreens.filter((monitor: BehaviorScreen) => monitor.MachinePhysicalAddress === location.PhysicalAddress));
    }
    this.updateLocationsLink();
  }

  setUserLocation(data: Machine[], value: Machine) {
    if (!value) {
      this.location = this.allLocations;
    } else if (data && data.length) {
      this.location = data[0];
    } else {
      this.location = value;
    }
    this.locations.forEach((loc) => (loc.IsSelected = loc.PhysicalAddress === this.location.PhysicalAddress));
    this.screenStateService.updateMonitorVisibility(
      this.locationFilter,
      this.nameFilter
    );
  }

  setCurrentCategory(selectedCategories: MachineCategory[]): void {
    this.screenStateService.selectedScreensChanged.next(
      this.selectedScreens.filter(monitor => selectedCategories.some(category => category.Id === monitor.Machine.CategoryId)));
  }

  relaunchClient() {
    this.subscriptions.add(
      this.relaunchConfirmationRequest()
        .subscribe((isToRestart: boolean) => {
          if (isToRestart) {
            this.stopPlaylistOnSelectedMachines();
          }
        })
    );
  }

  private stopPlaylistOnSelectedMachines(): void {
    this.isRelaunching = true;
    const selectedPhysicalAdresses = this.getSelectedLocationPhysicalAddresses();
    selectedPhysicalAdresses.forEach(physicalAdress => {
      this.subscriptions.add(
        this.machineService.stopAllPlaylistsOnMachine(physicalAdress).subscribe(() => {
          this.relaunchClientOnSelectedMachines(physicalAdress)
        })
      );
    });
  }

  private relaunchClientOnSelectedMachines(physicalAdress: string): void {
    this.subscriptions.add(
      this.playlistItemsService.relaunchClient([physicalAdress]).pipe(
        mergeMap(() => this.monitorHub.ScreenCacheAdded),
        take(1)
      )
      .subscribe(() => {
        this.isRelaunching = false;
      })
    );
  }

  private relaunchConfirmationRequest(): Observable<boolean> {
    return this.alertService.show(
      new ConfirmationConfig({
        header: DialogMessagesEnum.ApplyConfirmationHeader,
        message: DialogMessagesEnum.RelaunchClientConfirmationMessage,
        cancelVisible: true
      })
    );
  }

  private getSelectedLocationPhysicalAddresses(): string[] {
    const selectedPhysicalAddresses: string[] = [];
    if (
      this.location.Title === AppConstants.ALL_LOCATIONS_TITLE &&
      this.location.IsSelected
    ) {
      this.locations.forEach((location) => {
        if (location.PhysicalAddress) {
          selectedPhysicalAddresses.push(location.PhysicalAddress);
        }
      })
    }
    else {
      selectedPhysicalAddresses.push(this.location.PhysicalAddress);
    }

    return selectedPhysicalAddresses;
  }

  onFullScreenClick(): void {
    if (!this.monitorChangeViewService.selectedForFullScreenChanged.value && this.screens && this.screens.length) {
      this.monitorChangeViewService.initChangeImageModel(this.screens[0], true);
    }
    this.viewMode = this.viewModes.FullScreen;
  }

  onDetailScreenClick(): void {
    this.subscriptions.add(this.remoteControlService.closeRemoteControlSession().subscribe());
    this.viewMode = this.viewModes.Monitors;
  }

  onMonitorsSmallClick(): void {
    this.subscriptions.add(this.remoteControlService.closeRemoteControlSession().subscribe());
    this.viewMode = this.viewModes.MonitorsSmall;
  }

  onMonitorsCalendarClick(): void {
    this.subscriptions.add(this.remoteControlService.closeRemoteControlSession().subscribe());
    this.viewMode = this.viewModes.Calendar;
  }

  onMonitorsListClick(): void {
    this.subscriptions.add(this.remoteControlService.closeRemoteControlSession().subscribe());
    this.viewMode = this.viewModes.List;
  }

  private checkIfSelectedLocationExists(locations: Machine[]): void {
    if (!this.locationFilter || !this.locationFilter.PhysicalAddress) {
      return;
    }

    const idx = locations.findIndex(
      (location) =>
        location.PhysicalAddress === this.locationFilter.PhysicalAddress
    );
    if (idx === -1) {
      this.setUserLocation(locations, null);
      this.setCurrentLocation(this.allLocations);
    }
  }

  private updateLocationsLink(): void {
    setTimeout(() => (this.locations = [...(this.locations ? this.locations : [])]));
  }

  private updateIdentification(data: BehaviorScreen[]) {
    if (data && data.length) {
      this.isIdentifyEnabled = data.some(screen => screen.ScreenState?.IsSplashEnabled);
      this.isSimplifiedIdentifyEnabled = data.some(screen => screen.ScreenState?.IsSimplifiedSplashEnabled);
    }
  }
}
